import { useState } from "react";

import { Lightbulb } from "lucide-react";

import { GuideDetailModal, GuideModal } from "./GuideModal";
import { GUIDE_STEPS } from "./guideSteps";

import type { StepDone, StepId } from "./guideSteps";
import type { useGuideState } from "./guideStorage";

/**
 * Where the checklist sits; only what can be judged differs:
 * - `empty`: no class yet.
 * - `list`: classes exist, but only step 1 can be judged without opening one.
 * - `class`: inside a class; every step is judged for that class.
 */
export type ChecklistMode = "empty" | "list" | "class";

const statusText = (status: boolean | null) =>
  status ? "완료" : status === null ? "학급에 들어가서 확인" : "아직 안 함";

/** A one-line tooltip bar: progress dots, the next step's tip and shortcuts. */
export const GettingStartedChecklist = ({
  guide,
  mode,
  done,
  onShortcut,
}: {
  guide: ReturnType<typeof useGuideState>;
  mode: ChecklistMode;
  done: StepDone;
  onShortcut: (id: StepId) => void;
}) => {
  const { state, setHidden } = guide;
  const [detailOpen, setDetailOpen] = useState(false);
  if (state.hidden) {
    return null;
  }
  const doneCount = GUIDE_STEPS.filter((step) => done[step.id] === true).length;
  const total = GUIDE_STEPS.length;
  // Without a class open only step 1 has a shortcut that works.
  const nextId =
    (mode === "list"
      ? done.class === false
        ? "class"
        : null
      : GUIDE_STEPS.find((step) => done[step.id] === false)?.id) ?? null;
  const next = GUIDE_STEPS.find((step) => step.id === nextId) ?? null;
  const allDone = doneCount === total;

  const hideButton = (
    <button
      type="button"
      className="canvaschool-link-button"
      onClick={() => setHidden(true)}
    >
      가이드 숨기기
    </button>
  );

  if (allDone) {
    return (
      <section
        className="canvaschool-guide canvaschool-no-print"
        aria-label="시작 가이드"
      >
        <Lightbulb size={18} aria-hidden="true" />
        <p className="canvaschool-guide-text">시작 가이드를 모두 마쳤습니다</p>
        {hideButton}
      </section>
    );
  }

  return (
    <section
      className="canvaschool-guide canvaschool-no-print"
      aria-label="시작 가이드"
    >
      <Lightbulb size={18} aria-hidden="true" />
      <strong className="canvaschool-guide-name">시작 가이드</strong>
      <ol className="canvaschool-guide-dots">
        {GUIDE_STEPS.map((step, index) => {
          const status = done[step.id];
          return (
            <li
              key={step.id}
              data-step={step.id}
              title={step.title}
              className={
                status
                  ? "is-done"
                  : step.id === nextId
                  ? "is-next"
                  : status === null
                  ? "is-unknown"
                  : ""
              }
            >
              <span className="canvaschool-visually-hidden">
                {index + 1}단계 {step.title} {statusText(status)}
              </span>
            </li>
          );
        })}
      </ol>
      <span className="canvaschool-guide-count">
        {doneCount}/{total}
      </span>
      <p className="canvaschool-guide-text">
        {next ? (
          <>
            <strong>{next.title}</strong>: <span>{next.tip}</span>
          </>
        ) : (
          "학급에 들어가 다음 단계를 진행하세요"
        )}
      </p>
      <span className="canvaschool-guide-actions">
        {next && (
          <button
            type="button"
            className="canvaschool-btn-primary"
            onClick={() => onShortcut(next.id)}
          >
            {next.shortcut}
          </button>
        )}
        <button
          type="button"
          className="canvaschool-link-button"
          onClick={() => setDetailOpen(true)}
        >
          자세히 보기
        </button>
        {hideButton}
      </span>
      {detailOpen &&
        (next ? (
          <GuideDetailModal
            title={next.title}
            intro={next.description}
            items={next.details}
            onClose={() => setDetailOpen(false)}
          />
        ) : (
          <GuideModal onClose={() => setDetailOpen(false)} />
        ))}
    </section>
  );
};
